import type { InMemoryCacheConfig } from '@apollo/client';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { ClientAwarenessLink } from '@apollo/client/link/client-awareness';
import generatedPossibleTypes from '__generated__/possibleTypes.json';
import type { IgnorableError } from 'platform/apolloLink';
import {
  batchSplitterLink,
  dataIdFromObject,
  errorReporterLink,
  raiseRetryableGQLErrorsLink,
  redirectUnauthenticatedErrorsLink,
  retryLink,
  trackTimeToFirstQueryLink,
} from 'platform/apolloLink';
import { globalTrackError } from 'platform/observability';

const { possibleTypes } = generatedPossibleTypes;

const cacheOptions: InMemoryCacheConfig = {
  // When using fragments with unions and interfaces, the possible types for each polymorphic type must be passed to Apollo client.
  // Without this, fragments with unions and interfaces will not have their data cached and returned properly.
  // Documentation: https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
  // possibleTypes.json is generated based on the staging server's schema by running `pnpm generate-possibleTypes`.
  possibleTypes,
  typePolicies: {
    AssignedAgent: {
      keyFields: ['agentId'],
      merge: true,
    },
    Brokerage: {
      keyFields: ['brokerageId'],
      merge: true,
    },
    HomeComp: {
      keyFields: ['addressUuid'],
      merge: true,
    },
    HomeDetailsResult: {
      keyFields: ['addressUuid'],
      merge: true,
    },
    MarketplaceProperty: {
      keyFields: ['addressToken'],
      merge: true,
    },
    OdProtosHomeDataStore_HomeDetail: {
      keyFields: ['homeDetailId'],
      merge: true,
    },
    OdProtosValidataData_FloorPlanScanEligibility: {
      keyFields: ['addressToken'],
      merge: true,
    },
    OdProtosVariantV2Services_GetExperimentImpressionsResponse_VariantsEntry: {
      keyFields: ['key'],
    },
    OdProtosVariantV2Services_GetExperimentVariantsResponse_VariantsEntry: {
      keyFields: ['key'],
    },
    OdProtosWebData_DiligenceVisitStep: {
      keyFields: ['visitId'],
      merge: true,
    },
    OdProtosBuyerServicesBuyer_Buyer_Query: {
      merge: true,
    },
    HomeGuideQuery: {
      merge: true,
    },
    // Answers has no stable ID and is only ever fetched as a child of SellerInput
    // (keyed by uuid). Embed it in the parent and replace wholesale on merge.
    OdProtosSellReceptionData_Answers: {
      keyFields: false,
      merge: true,
    },
    OdProtosSellSellerServices_Seller_Query: {
      merge: true,
    },
    OdProtosWebServicesSellDirect_SellDirect_Query: {
      merge: true,
    },
    OdProtosPartnershipServices_Partnership_Query: {
      merge: true,
    },
    // Enables merging of multiple sellerTransaction operations into the SellerProductsQuery cache
    SellerProductsQuery: {
      merge: true,
    },
    // Enables merging of multiple operations on the WebQuery into the WebQuery cache (getContactInfo, etc)
    WebQuery: {
      merge: true,
    },
  },
};

/**
 * https://grpc.github.io/grpc/core/md_doc_statuscodes.html
 *
 * @internal
 */
export const BLOCKED_RETRY_STATUS_NUMBERS = [
  // OK
  0,
  // INVALID_ARGUMENT
  3,
  // NOT_FOUND
  5,
  // PERMISSION_DENIED
  7,
  // UNAUTHENTICATED
  16,
];

const IGNORABLE_ERRORS_LIST: Array<IgnorableError> = [
  // NOTE: This occurs when markets are added and do not have adequate data yet.
  {
    value: 'No entry found for market',
  },
  // NOTE: This occurs when markets are added and do not have adequate data yet.
  {
    value: 'No off market listings found',
  },
  // NOTE: This occurs Ballpark just doesn't have home price data for an unqualified home
  {
    value: 'BallparkQuery: 5 NOT_FOUND',
  },
  // NOTE: This occurs when a user has not provided a phone number when creating an offer
  {
    value: 'Unable to save offer',
  },
  // NOTE: This occurs when someone tries to view a lead owned by a different
  // customer. This is an expected error and is handled gracefully, so no need
  // to report it.
  {
    value: 'GetLatestLead - failed to find lead',
  },
  // NOTE: This occurs when we try to fetch a trait for a lead for whom Journey
  // currently has no traits. This is expected and handled gracefully.
  {
    value: '5 NOT_FOUND: GetTraitForIdentifier',
  },
];

type ClientOptions = { batch?: boolean; uri?: string } & Partial<
  Omit<ApolloClient.Options, 'link' | 'cache' | 'defaultOptions'>
>;

const clientOptions = (uri: string, options?: ClientOptions): ApolloClient.Options => {
  let batch: boolean | undefined;
  let clientOptions: Partial<ApolloClient.Options> | undefined;
  if (options) {
    ({ batch, ...clientOptions } = options);
  }

  const apolloCache = new InMemoryCache({ ...cacheOptions, dataIdFromObject });

  return {
    link: ApolloLink.from([
      new ClientAwarenessLink({
        clientAwareness: {
          name: 'cake',
        },
      }),
      trackTimeToFirstQueryLink(),
      errorReporterLink({
        ignorableErrorsList: IGNORABLE_ERRORS_LIST,
        trackError: globalTrackError,
      }),
      retryLink(),
      raiseRetryableGQLErrorsLink(BLOCKED_RETRY_STATUS_NUMBERS),
      redirectUnauthenticatedErrorsLink(),
      batch ? batchSplitterLink(uri) : new HttpLink({ uri, credentials: 'include' }),
    ]),
    cache: apolloCache,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
      watchQuery: {
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: false,
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    ...clientOptions,
  };
};

const DEFAULT_GRAPHQL_CLIENT_URI = '/api/graphql';

/**
 * Set up an ApolloClient given a URI
 *
 * This client provides support for HTTP endpoints
 */
export const createApolloClient = (options?: ClientOptions): ApolloClient =>
  new ApolloClient(clientOptions(options?.uri ?? DEFAULT_GRAPHQL_CLIENT_URI, options));
